import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ArrowLeft, KeyRound } from "lucide-react";

const LoginPage = () => {
  const [step, setStep] = useState("email"); // email, password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Bitte geben Sie Ihre E-Mail ein");
      return;
    }
    setStep("password");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Bitte geben Sie Ihr Passwort ein");
      return;
    }

    setIsLoading(true);
    try {
      await login(email, password);
      navigate("/document");
    } catch (error) {
      const message = error.response?.data?.detail || "Anmeldung fehlgeschlagen";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep("email");
    setPassword("");
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      {/* Background with abstract shapes */}
      <div className="fixed inset-0 bg-[#f2f2f2] -z-10">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#c3d7e8" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#a8d4e6" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="grad2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#d4e5f0" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#b8d4e8" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <polygon points="0,400 600,200 400,600 0,800" fill="url(#grad1)" />
          <polygon points="1920,0 1400,300 1600,700 1920,400" fill="url(#grad2)" />
          <polygon points="800,900 1200,700 1100,1080 700,1080" fill="url(#grad1)" />
        </svg>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-[440px]">
          {/* Login Card */}
          <div className="bg-white shadow-[0_2px_6px_rgba(0,0,0,0.2)] p-11">
            {/* Logo */}
            <div className="mb-4 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_flexipfleg-login/artifacts/d5s7aion_image.png" 
                alt="Holding" 
                className="h-14"
                data-testid="holding-logo"
              />
            </div>

            {step === "email" ? (
              <>
                {/* Email Step */}
                <h1 className="text-2xl font-semibold text-[#1b1b1b] mb-4" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
                  Anmelden
                </h1>
                
                <form onSubmit={handleEmailSubmit}>
                  <div className="mb-4">
                    <Input
                      type="email"
                      placeholder="E-Mail, Telefon oder Skype"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border-0 border-b border-[#666] rounded-none px-0 h-9 text-[15px] focus:border-b-[#0067b8] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[#666]"
                      disabled={isLoading}
                      autoFocus
                      data-testid="login-email-input"
                    />
                  </div>

                  <p className="text-sm text-[#1b1b1b] mb-1">
                    Kein Konto?{" "}
                    <Link to="/register" className="text-[#0067b8] hover:text-[#005a9e] hover:underline" data-testid="register-link">
                      Erstellen Sie jetzt eins!
                    </Link>
                  </p>

                  <Link to="/forgot-password" className="text-sm text-[#0067b8] hover:text-[#005a9e] hover:underline block mb-6" data-testid="forgot-password-link">
                    Sie können nicht auf Ihr Konto zugreifen?
                  </Link>

                  <div className="flex justify-end">
                    <Button 
                      type="submit"
                      className="bg-[#0067b8] hover:bg-[#005a9e] text-white rounded-none px-8 h-9 text-[15px] font-normal"
                      disabled={isLoading}
                      data-testid="login-next-button"
                    >
                      Weiter
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <>
                {/* Password Step */}
                <button 
                  onClick={handleBack}
                  className="flex items-center text-sm text-[#1b1b1b] hover:underline mb-4"
                  data-testid="back-button"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  {email}
                </button>

                <h1 className="text-2xl font-semibold text-[#1b1b1b] mb-2" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
                  Kennwort eingeben
                </h1>

                <form onSubmit={handlePasswordSubmit}>
                  <div className="mb-4">
                    <Input
                      type="password"
                      placeholder="Kennwort"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full border-0 border-b border-[#666] rounded-none px-0 h-9 text-[15px] focus:border-b-[#0067b8] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[#666]"
                      disabled={isLoading}
                      autoFocus
                      data-testid="login-password-input"
                    />
                  </div>

                  <Link to="/forgot-password" className="text-sm text-[#0067b8] hover:text-[#005a9e] hover:underline block mb-6" data-testid="forgot-password-link-2">
                    Kennwort vergessen?
                  </Link>

                  <div className="flex justify-end">
                    <Button 
                      type="submit"
                      className="bg-[#0067b8] hover:bg-[#005a9e] text-white rounded-none px-8 h-9 text-[15px] font-normal"
                      disabled={isLoading}
                      data-testid="login-submit-button"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Anmelden"
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>

          {/* Sign-in options */}
          <div className="bg-white shadow-[0_2px_6px_rgba(0,0,0,0.2)] mt-4 px-11 py-4">
            <button className="flex items-center text-sm text-[#1b1b1b] hover:underline w-full">
              <KeyRound className="h-5 w-5 mr-3 text-[#666]" />
              Anmeldeoptionen
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end items-center p-4 text-xs text-[#666] space-x-6">
        <a href="#" className="hover:underline">Nutzungsbedingungen</a>
        <a href="#" className="hover:underline">Datenschutz und Cookies</a>
        <button className="hover:underline">...</button>
      </div>
    </div>
  );
};

export default LoginPage;
